import { useEffect, useState } from "react";
import { TbEdit } from "react-icons/tb";
import FilePreview from "../renderFilePreview";
import TextFieldCustom from "../elements/textfieldCustom";
import CustomButton from "../elements/CustomButton/CustomButton";
import { useSnackbar } from "../SnackbarContext";
import { validateForm } from "../../utils/formValidation/validation";
import { buildOtherDetailsPayload } from "../../utils/buildUserPayload";
import UploadCard from "../uploadCard";
import axiosInstance from "../../configuration/request";
import {
  otherDetailsRules,
} from "../../utils/formValidation/validationRules";

const documents = [
  {
    heading: "Upload driving license",
    documentType: "PDF, JPEG or JPG",
    name: "driving_license",
  },
  {
    heading: "Upload Insurance (if any)",
    documentType: "PDF, JPEG or JPG",
    name: "insurance",
  },
];

export default function DeliverySection({ deliveryDetails, onSave }) {
  const showSnackbar = useSnackbar();
  const [isEditing, setIsEditing] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [missingDocs, setMissingDocs] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const requiredDocs = ["driving_license", "insurance"];
  const [formData, setFormData] = useState({
    vechicalType: "",
    licencePlateNumber: "",
  });
  const [initialFormData, setInitialFormData] = useState({});
  const [initialUploadedFiles, setInitialUploadedFiles] = useState([]);

 useEffect(() => {
  if (deliveryDetails) {
    const initialData = {
      vechicalType: deliveryDetails?.vehicle_type || '',
      licencePlateNumber: deliveryDetails?.vehicle_number || '',
    };
    setFormData(initialData);
    setInitialFormData(initialData); 

    const initialDocs = deliveryDetails.uploadDoc ?? [];
    setUploadedFiles(initialDocs);
    setInitialUploadedFiles(initialDocs); 
  }
}, [deliveryDetails]);

  const handleChange = (field) => (e) => {
    const value = e.target?.value ?? e;
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleFileSelect = async (file, fileName) => {
    try {
      const form = new FormData();
      form.append("file", file);
      const response = await axiosInstance.post("/customer/upload/file", form);
      const fileUrl = response.data?.url || "";

      setUploadedFiles((prev) => {
        const filtered = prev.filter((doc) => doc.name !== fileName);
        return [...filtered, { name: fileName, fileUrl }];
      });
      setMissingDocs((prev) => prev.filter((doc) => doc !== fileName));
    } catch (error) {
      console.error("Upload failed:", error);
      showSnackbar("File upload failed", "error");
    }
  };

  const handleSave = async () => {
    const errors = validateForm(formData, otherDetailsRules);
    setFormErrors(errors);
    const uploadedNames = uploadedFiles.map((f) => f.name);
    const missing = requiredDocs.filter((doc) => !uploadedNames.includes(doc));
    setMissingDocs(missing);

    if (Object.keys(errors).length === 0 && missing.length === 0) {
      const payload = buildOtherDetailsPayload({
        otherDetails: formData,
        uploadedDocs: uploadedFiles,
      });
      if (onSave) onSave(payload);
      setIsEditing(false);
    } else {
      showSnackbar("Please fix errors and upload required docs", "error");
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <div className="font-semibold text-lg">Vehical Details</div>
        <div className="flex gap-2">
          <CustomButton
              onClick={() => {
              if (isEditing) {
                // If we are cancelling, re-initialize the form data
                setFormData(initialFormData);
                setUploadedFiles(initialUploadedFiles);
              }
              setIsEditing(!isEditing);
            }}
            customeClass="primary-custom-button w-[100px] h-[35px]"
          >
            <span className="mr-1 text-xl">{isEditing ? "" : <TbEdit />}</span>
            <span className="text-base">{isEditing ? "Cancel" : "Edit"}</span>
          </CustomButton>
          {isEditing && (
            <CustomButton
              onClick={handleSave}
              customeClass="gradient-button w-[100px] h-[35px]"
            >
              Save
            </CustomButton>
          )}
        </div>
      </div>

      {isEditing ? (
        <>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { field: "vechicalType", label: "Vehicle type*" },
              { field: "licencePlateNumber", label: "License Plate Number*" },
            ].map(({ field, label }) => (
              <TextFieldCustom
                key={field}
                label={label}
                fullWidth
                value={formData[field]}
                onChange={handleChange(field)}
                error={!!formErrors[field]}
                helperText={formErrors[field] || " "}
              />
            ))}
          </div>
          <div className="mt-4">
            <div className="text-sm font-semibold mb-2">Upload Documents</div>
            <div className="w-full flex gap-3 flex-wrap">
              {documents.map((doc, idx) => {
                const uploadedDoc = uploadedFiles?.find(
                  (f) => f.name === doc.name
                );
                const hasError = missingDocs.includes(doc.name);
                return (
                  <div key={idx} className="flex flex-col">
                    <UploadCard
                      doc_name={doc.name}
                      heading={doc.heading}
                      documentType={doc.documentType}
                      onFileSelect={(file) => handleFileSelect(file, doc.name)}
                      fileUploaded={!!uploadedDoc}
                      fileUrl={uploadedDoc?.fileUrl}
                      hasError={hasError}
                    />
                    {hasError && (
                      <span className="text-xs text-[#d32f2f] mt-1 ml-1">
                        {doc.name} is required.
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="grid md:grid-cols-3 gap-y-2 text-sm text-gray-700">
            <div>
              <strong>Vehicle type</strong>
              <div className="mt-1">{formData.vechicalType || "-"}</div>
            </div>
            <div>
              <strong>License Plate Number</strong>
              <div className="mt-1">{formData.licencePlateNumber || "-"}</div>
            </div>
          </div>
          <div className="mt-4">
            <div className="text-sm font-semibold mb-2">Uploaded Documents</div>
            <div className="flex gap-3 flex-wrap">
              {uploadedFiles.length > 0 &&
                uploadedFiles?.map((doc, index) => {
                  const fileName = doc.name || `Document ${index + 1}`;
                  return <FilePreview key={index} file={doc} />;
                })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
